/**
 * @file    FieldItemCard.tsx
 * @scope   VIEW
 * @summary A single field row: collapsible card that hosts the Details and Filters tabs and shows
 *          validation state for the field and its filters.
 * @consumes FieldsManager models, field/filter validation, projectFrequency constants, sub-tabs
 * @produces `FieldItemCard` component
 * @sideEffects None — presentational; edits bubble up via callbacks.
 * @notes The pinned Project Frequency field renders read-only (only its display name is editable).
 */
import React, { useState } from 'react';
import { Icon } from '@cx/ui';
import Accordion from 'core-ui/accordionDrawer/accordionDrawer';
import Tippy from '@tippyjs/react';
import { SelectedFieldModel, FieldStateModel } from 'models/Enterprise/FieldsManagerModel';
import { AdvancedFilterFieldConfig } from 'models/WidgetModel';
import { isFieldItemInvalid } from '../../utils/validation/fieldValidation';
import { areFiltersInvalid } from '../../utils/validation/filterValidation';
import DataValidationTip from 'components/DataValidation/DataValidationTip';
import {
  PROJECT_FREQUENCY_FIELD_NAME,
  PROJECT_FREQUENCY_LABEL,
} from '../../utils/payload/projectFrequency';
import FieldDetailsTab from './FieldDetailsTab';
import FiltersTab from './FiltersTab';
import './FiltersTab.scss';
import './FieldCardTabs.scss';
import 'tippy.js/dist/tippy.css';

/** Truncates a string to a maximum length, appending ellipsis if it exceeds the limit. */
const truncateText = (text: string, maxLength = 20): string =>
  text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

type FieldItemCardTab = 'details' | 'filters';

interface FieldItemCardProps {
  field: SelectedFieldModel;
  index: number;
  fieldState: FieldStateModel;
  isHighlighted: boolean;
  isExpanded: boolean;
  isTouched: boolean;
  availableFields: { value: string; label: string }[];
  calculationTypes: { value: string; label: string }[];
  /** Available filter fields from Config.Filters.AdvancedFilters */
  availableFilterFields: AdvancedFilterFieldConfig[];

  /** Callback ref — the parent stores a Map<index, HTMLDivElement>. */
  innerRef: (el: HTMLDivElement | null) => void;

  onRemove: (index: number) => void;
  onFieldSelect: (index: number, option: any) => void;
  onDisplayNameChange: (index: number, value: string) => void;
  onCalculationTypeChange: (index: number, option: any) => void;
  onPrecisionChange: (index: number, option: any) => void;
  onNormalizeChange: (index: number, checked: boolean) => void;
  onStatTestingChange: (index: number, checked: boolean) => void;
  onIsL1CategoryChange: (index: number, checked: boolean) => void;
  onReorder: (draggedIndex: number, targetIndex: number) => void;
  onAccordionToggle: (index: number) => void;
  onBlur: (e: React.FocusEvent<HTMLDivElement>, index: number) => void;
  onAdvancedSettings?: (field: SelectedFieldModel, fieldIndex: number) => void;
  /** Called when user clicks Edit on a filter or Add Filter — navigates to FiltersSettingsManager */
  onEditFilter: (fieldIndex: number, filterId: string | null) => void;
  onFilterRemove: (index: number, filterId: string) => void;
  /** When set, the card starts with this tab active (used when returning from filter editing) */
  defaultActiveTab?: 'details' | 'filters';
}

const FieldItemCard: React.FC<FieldItemCardProps> = ({
  field,
  index,
  fieldState,
  isHighlighted,
  isExpanded,
  isTouched,
  availableFields,
  calculationTypes,
  availableFilterFields,
  innerRef,
  onRemove,
  onFieldSelect,
  onDisplayNameChange,
  onCalculationTypeChange,
  onPrecisionChange,
  onNormalizeChange,
  onStatTestingChange,
  onIsL1CategoryChange,
  onReorder,
  onAccordionToggle,
  onBlur,
  onAdvancedSettings,
  onEditFilter,
  onFilterRemove,
  defaultActiveTab,
}) => {
  const isInvalid = isFieldItemInvalid(field, index, new Set(isTouched ? [index] : []));
  const [activeTab, setActiveTab] = useState<FieldItemCardTab>(defaultActiveTab ?? 'details');
  const filters = field.filters ?? [];

  /**
   * Filter-level validation: mirrors field-level validation.
   * A filter is invalid when touched and at least one filter is missing a selected value.
   */
  const isFiltersInvalid = areFiltersInvalid(filters, isTouched);

  /**
   * Combined card-level invalid state:
   * the card shows as invalid if EITHER the field details OR the filters are invalid.
   * This propagates to the outer border and the DataValidationTip tooltip.
   */
  const isCardInvalid = isInvalid || isFiltersInvalid;
  const displayNameValue = field.displayName?.trim() ? field.displayName : 'No Display Name';
  const fieldNameValue = field.field?.fieldName?.trim()
    ? field.field.fieldName
    : `Field ${index + 1}`;
  const isProjectFrequency = field.isProjectFrequency === true;
  const taglineValue = isProjectFrequency ? PROJECT_FREQUENCY_FIELD_NAME : fieldNameValue;

  return (
    <Tippy
      content={
        <div>
          <div className="tooltip-title">{displayNameValue}</div>
          <div className="tooltip-text">{taglineValue}</div>
        </div>
      }
      disabled={
        displayNameValue === 'No Display Name' ||
        displayNameValue === 'New Field' ||
        taglineValue === `Field ${index + 1}`
      }
      placement="left"
      className="tooltip-container"
    >
      <div
        ref={innerRef}
        tabIndex={-1}
        draggable={!isProjectFrequency}
        onDragStart={(e) => {
          if (isProjectFrequency) {
            e.preventDefault();
            return;
          }
          e.dataTransfer.setData('fieldIndex', String(index));
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const draggedIndex = parseInt(e.dataTransfer.getData('fieldIndex'), 10);
          onReorder(draggedIndex, index);
        }}
        onBlur={(e) => onBlur(e, index)}
        className={`field-item${isHighlighted ? ' field-item--highlighted' : ''}${
          isCardInvalid ? ' enterprise-container--invalid' : ''
        }${isProjectFrequency ? ' field-item--project-frequency' : ''}`}
      >
        {/* Header Row: Drag Icon and Delete Icon */}
        <div className="field-item-header">
          {isProjectFrequency ? (
            <span className="project-frequency-badge">{PROJECT_FREQUENCY_LABEL}</span>
          ) : (
            <>
              <div className="dragHandle" title="Drag to reorder">
                <Icon name="drag_indicator" />
              </div>
              <div
                className="diy-drawer-icon delete-icon"
                onClick={() => onRemove(index)}
                title="Delete field"
              >
                <Icon name="system-delete" />
              </div>
            </>
          )}
          <div className="field-item-validation">
            {isCardInvalid && (
              <DataValidationTip
                message={
                  isInvalid && isFiltersInvalid
                    ? 'This field has missing parameters and incomplete filters.'
                    : isFiltersInvalid
                    ? 'One or more filters are missing a selected value.'
                    : 'This field has missing parameters.'
                }
                kind="error"
                placement="bottom"
              />
            )}
          </div>
        </div>

        {/* Accordion */}
        <Accordion
          title={truncateText(displayNameValue)}
          tagline={truncateText(taglineValue)}
          arrowIcon={<Icon name="arrow-expand" size={36} />}
          isExpanded={isExpanded}
          onToggle={() => onAccordionToggle(index)}
        >
          <div className="field-card-tabs">
            {fieldState.Filters.visible && (
              <div className="field-card-tabs__header">
                <button
                  type="button"
                  className={`field-card-tabs__tab${
                    activeTab === 'details' ? ' field-card-tabs__tab--active' : ''
                  }${isInvalid ? ' field-card-tabs__tab--invalid' : ''}`}
                  onClick={() => setActiveTab('details')}
                >
                  Field details
                </button>
                <button
                  type="button"
                  className={`field-card-tabs__tab${
                    activeTab === 'filters' ? ' field-card-tabs__tab--active' : ''
                  }${isFiltersInvalid ? ' field-card-tabs__tab--invalid' : ''}`}
                  onClick={() => setActiveTab('filters')}
                  disabled={fieldState.Filters.disabled}
                >
                  Filters ({filters.length})
                </button>
              </div>
            )}
            <div className="field-card-tabs__content">
              {activeTab === 'details' && (
                <FieldDetailsTab
                  field={field}
                  index={index}
                  fieldState={fieldState}
                  isTouched={isTouched}
                  availableFields={availableFields}
                  calculationTypes={calculationTypes}
                  onFieldSelect={onFieldSelect}
                  onDisplayNameChange={onDisplayNameChange}
                  onCalculationTypeChange={onCalculationTypeChange}
                  onPrecisionChange={onPrecisionChange}
                  onNormalizeChange={onNormalizeChange}
                  onStatTestingChange={onStatTestingChange}
                  onIsL1CategoryChange={onIsL1CategoryChange}
                  onAdvancedSettings={onAdvancedSettings}
                />
              )}
              {activeTab === 'filters' && (
                <FiltersTab
                  filters={filters}
                  availableFilterFields={availableFilterFields}
                  disabled={fieldState.Filters.disabled}
                  onEditFilter={(filterId) => onEditFilter(index, filterId)}
                  onRemoveFilter={(id) => onFilterRemove(index, id)}
                />
              )}
            </div>
          </div>
        </Accordion>
      </div>
    </Tippy>
  );
};

export default FieldItemCard;
